import { VuexModule, Module, Action, Mutation } from "vuex-module-decorators";
import JobsApi from "@/api/jobs-api";
import { TimeUtil } from "@/helpers/TimeUtil";
import dayjs from "dayjs";
import {
  companyModule,
  installationsModule,
  partnersModule,
} from "@/store/modules/store-accessor";
import _ from "lodash";
import { deepTrim, showRecurrenceDaysOptions } from "@/helpers/data";
import { normalizeAssigneeIdsForSubmit } from "@/helpers/assignees";

@Module({ namespaced: true, name: "jobs" })
export default class JobsModule extends VuexModule {
  public jobs: JobDto[] = [];
  public currJob: JobDto = {} as JobDto;
  public isLoadingTable = true;
  public filters = "";
  public jobTotals: any = {};
  public queryObject: any = {};
  public search = "";

  @Action
  async getJobPendingBalance(jobId: string): Promise<number> {
    const jobApi = new JobsApi();
    const pendingBalance = await jobApi.getJobPendingBalance(jobId);
    return pendingBalance.pending;
  }

  @Action({ commit: "setJobInfo" })
  async getJobs(query: string): Promise<JobDto[]> {
    this.setIsLoadingTable(true);
    const jobApi = new JobsApi();
    const jobs = await jobApi.getAllJobs(query);

    return jobs;
  }

  @Action({ commit: "setJobTotals" })
  async getJobTotals(query: string): Promise<JobDto[]> {
    const jobApi = new JobsApi();
    const jobTotals = await jobApi.getJobsTotals(query);
    return jobTotals;
  }

  @Action({ commit: "setJobDetails" })
  async getJobDetails(currentJobId: string): Promise<JobDto> {
    const jobApi = new JobsApi();
    const jobInfo = await jobApi.getJob(currentJobId);
    return jobInfo;
  }

  @Action
  async createJobWithActions(data: Record<string, any>) {
    data = deepTrim(data);
    let jobData = _.cloneDeep(data);

    jobData.assignee_id = data.assignee_id.id;
    jobData.actions = data.type_id;

    if (!jobData.is_paid) {
      delete jobData.payments;
    }

    if (data.installation_item_id && data.installation_item_id.id) {
      jobData.item_type_text = jobData.installation_item_id.item_type_text;
      jobData.item_make = jobData.installation_item_id.item_make;
      jobData.item_model = jobData.installation_item_id.item_model;
      jobData.item_serial_no = jobData.installation_item_id.item_serial_no;
      jobData.installation_item_id = data.installation_item_id.id;
    } else {
      delete jobData.installation_item_id;
    }

    jobData.actions.map((item: Record<string, any>) => {
      item.unit = item.is_time_charged ? item.unit.unit : null;
    });

    delete jobData.is_paid;

    if (jobData.expenses?.length) {
      jobData.expenses.forEach((item: Record<string, any>) => {
        item.expense_label_id = item?.expense_label_id?.id ?? null;
      });
    }

    const jobApi = new JobsApi();

    jobData = deepTrim(jobData);
    const jobId = await jobApi.createJobWithActions(jobData);

    if (data.task_id != null) {
      await installationsModule.invalidateInstallationTab({
        tabs: ['jobs', 'items', 'photos'],
        installationId: data.installation_id,
      });
      await installationsModule.getInstallationDetailsTasks(data.installation_id);
    } else {
      await installationsModule.getInstallationDetailsJobs(data.installation_id);
      await installationsModule.invalidateInstallationTab({
        tabs: ['items', 'photos'],
        installationId: data.installation_id,
      });
    }

    if (jobData.partner_id) {
      partnersModule.getPartnersInfo(jobData.partner_id);
    }
    return jobId;
  }

  @Action
  async editJob(data: Record<string, any>) {
    data = deepTrim(data);
    let job_data = _.cloneDeep(data);

    //make cost null for new jobs. Keep cost for legacy jobs.
    if (job_data.cost_disabled) job_data.cost = null;

    if (job_data.installation_item_id && job_data.installation_item_id.id) {
      job_data.item_type_text = job_data.installation_item_id.item_type_text;
      job_data.item_make = job_data.installation_item_id.item_make;
      job_data.item_model = job_data.installation_item_id.item_model;
      job_data.item_serial_no = job_data.installation_item_id.item_serial_no;
      job_data.installation_item_id = job_data.installation_item_id.id;
    } else {
      delete job_data.installation_item_id;
    }

    const jobApi = new JobsApi();

    job_data = deepTrim(job_data);
    await jobApi.updatejob(job_data.id, job_data);
    await installationsModule.getInstallationDetailsJobs(data.installation_id);
    await installationsModule.invalidateInstallationTab({tabs: ['appointments', 'items', 'photos'], installationId: data.installation_id});
    if (data.partner_id) {
      partnersModule.getPartnersInfo(data.partner_id);
    }
  }

  @Action
  async updateJobComplete(data: Record<string, any>) {
    data = deepTrim(data);

    const jobApi = new JobsApi();

    await jobApi.updateJobComplete(data.id, data);
    await installationsModule.getInstallationDetailsJobs(data.installation_id);
    await installationsModule.invalidateInstallationTab({tabs: ['appointments', 'items', 'photos'], installationId: data.installation_id});
    if (data.partner_id) {
      partnersModule.getPartnersInfo(data.partner_id);
    }
  }

  @Action
  async deleteJob(data: Record<string, any>) {
    const jobApi = new JobsApi();
    await jobApi.deleteJob(data.id);
    await installationsModule.getInstallationDetailsJobs(data.installation_id);
    await installationsModule.invalidateInstallationTab({tabs: ['appointments', 'items', 'photos', 'tasks'], installationId: data.installation_id});
    if (data.partner_id) {
      partnersModule.getPartnersInfo(data.partner_id);
    }
  }

  @Action
  async createAppointmentToJob(data: Record<string, any>) {
    const taskId = data.task_id;

    data = deepTrim(data);
    data.assignee_id = data.assignee_id.id;
    data.assignee_ids = normalizeAssigneeIdsForSubmit(data.assignee_ids);
    data.type_id = data.type_id.id;
    data.duration = data.duration.dur;
    data.scheduled_start = data.scheduled_start
      ? dayjs(data.scheduled_start + " " + data.scheduled_start_time).toISOString()
      : null;
    delete data.scheduled_start_time;

    if(data.is_recurrent) {
      if(showRecurrenceDaysOptions(data)) {
        data.days_of_week = data.days_of_week.map((day: Record<string, string>) => day.value);
      } else {
        delete data.days_of_week;
      }

      data.recurrence_type = data.recurrence_type.value;
      data.end_type = data.end_date ? 'until_date' : 'none';
    } else {
      delete data.days_of_week;
      delete data.recurrence_type;
      data.interval;
      data.occurrence_count;
    }

    const jobApi = new JobsApi();
    await jobApi.createJobAppointment(data.id ?? data.job_id, data);

    if (taskId != null) {
      await installationsModule.invalidateInstallationTab({
        tabs: ['jobs', 'appointments', 'items'],
        installationId: data.installation_id,
      });
      await installationsModule.getInstallationDetailsTasks(data.installation_id);
    } else {
      await installationsModule.getInstallationDetailsJobs(data.installation_id);
      await installationsModule.invalidateInstallationTab({
        tabs: ['appointments', 'items'],
        installationId: data.installation_id,
      });
    }
  }

  @Action
  async createActionsToJob(data: Record<string, any>) {
    data = deepTrim(data);
    data.assignee_id = data.assignee_id.id;
    data.actions = data.type_id;
    delete data.type_id;

    if (data.created_at_date && data.created_at_time) {
      data.created_at = dayjs(
        data.created_at_date + " " + data.created_at_time
      ).toISOString();
      delete data.created_at_date;
      delete data.created_at_time;
    }

    data.actions.map((item: Record<string, any>) => {
      item.unit = item.is_time_charged ? item.unit.unit : null;
    });

    const jobApi = new JobsApi()
    await jobApi.createJobActions(data.id, data)
    await installationsModule.getInstallationDetailsJobs(data.installation_id);
    await installationsModule.invalidateInstallationTab({tabs: ['items'], installationId: data.installation_id});

    // Update partners sideview (jobs tab)
    if (
      Object.keys(partnersModule.currentPartner).length > 0 &&
      partnersModule.currentPartner.info.partner_id
    ) {
      await partnersModule.getPartnersInfo(
        partnersModule.currentPartner.info.partner_id
      );
    }
  }

  @Action
  async createMaterialsToJob(data: Record<string, any>) {
    data = deepTrim(data);
    const material_data = _.cloneDeep(data);

    material_data.assignee_id = material_data.assignee_id.id;

    if (material_data.created_at_date && material_data.created_at_time) {
      material_data.created_at = dayjs(
        material_data.created_at_date + " " + material_data.created_at_time
      ).toISOString();
      delete material_data.created_at_date;
      delete material_data.created_at_time;
    }

    const jobApi = new JobsApi();
    await jobApi.createJobMaterials(material_data.id, material_data);
    await installationsModule.getInstallationDetailsJobs(data.installation_id);
    await installationsModule.makeInstallationSideviewCommonCalls(data.installation_id);

    // Update partners sideview (jobs tab)
    if (data.partner_id) {
      await partnersModule.getPartnersInfo(data.partner_id);
    }
  }

  @Action
  async createExpensesToJob(data: Record<string, any>) {
    data = deepTrim(data);
    const expensesData = _.cloneDeep(data);

    expensesData.assignee_id = expensesData.assignee_id.id;
    expensesData.payment_type_id = expensesData.payment_type.id;

    if(expensesData.expense_label_id) {
      expensesData.expense_label_id = expensesData.expense_label_id.id;
    }

    if (expensesData.created_at_date && expensesData.created_at_time) {
      expensesData.created_at = dayjs(
        expensesData.created_at_date + " " + expensesData.created_at_time
      ).toISOString();
      delete expensesData.created_at_date;
      delete expensesData.created_at_time;
    }

    const jobApi = new JobsApi();
    const expenseId = await jobApi.createJobExpenses(
      expensesData.id,
      expensesData
    );
    await installationsModule.getInstallationDetailsJobs(data.installation_id);
    await installationsModule.makeInstallationSideviewCommonCalls(data.installation_id);
    // Update expenses sideview (jobs tab)
    if (data.partner_id) {
      await partnersModule.getPartnersInfo(data.partner_id);
    }
    return expenseId;
  }

  @Action
  async createPaymentToJob(data: Record<string, any>) {
    data = deepTrim(data);

    data.assignee_id = data.assignee_id?.id ?? data.assignee_id;

    if (data.created_at_date && data.created_at_time) {
      data.created_at = dayjs(
        data.created_at_date + " " + data.created_at_time
      ).toISOString();
      delete data.created_at_date;
      delete data.created_at_time;
    }

    const jobApi = new JobsApi();
    await jobApi.createJobPayment(data.id, data);

    Promise.all([
      installationsModule.getInstallationDetailsJobs(data.installation_id),
      installationsModule.makeInstallationSideviewCommonCalls(data.installation_id),
      data.partner_id ? partnersModule.getPartnersInfo(data.partner_id) : null,
    ]).catch(() => undefined);
  }

  @Action
  async getCompletedJobPdfExportAction(data: Record<string, any>) {
    const jobApi = new JobsApi();
    const response = await jobApi.getCompletedJobPdfExport(data);
    const blob = new Blob([response], { type: "application/pdf" });
    return blob;
  }

  @Action
  async getJobsPdfExportAction({
    query,
    data,
  }: {
    query: string;
    data: Record<string, any>;
  }) {
    const jobApi = new JobsApi();
    const response = await jobApi.getJobsPdfExport(query, data);
    const blob = new Blob([response], { type: "application/pdf" });
    return blob;
  }

  @Action
  async createBulkPayments(data: BulkJobPaymentsRequestDto) {
    const jobApi = new JobsApi();

    return jobApi.createBulkPayments(deepTrim(data) as BulkJobPaymentsRequestDto);
  }

  @Mutation
  public setJobInfo(jobs: JobDto[]): void {
    try {
      const installationItemDescriptionIsEnabled = (companyModule.company?.preferences as any)?.installation_item_description;

      if (installationItemDescriptionIsEnabled) {
        this.jobs = jobs.map(item => ({
          ...item,
          job_item: item.job_item ? `${item.item_description ?? ''}${item.item_description ? ' | ' : ''}${item.job_item}` : '-'
        }));
      } else {
        this.jobs = jobs;
      }

      this.isLoadingTable = false;
    } catch (error) {
      this.isLoadingTable = false;
    }
  }

  @Mutation
  public clearJobInfo(): void {
    this.jobs = [];
    this.jobTotals = {};
  }

  @Mutation
  public setJobTotals(jobTotals: any): void {
    this.jobTotals = jobTotals;
  }

  @Mutation
  public setJobDetails(job: JobDto): void {
    this.currJob = job;
  }

  @Mutation
  public setIsLoadingTable(isLoadingTable: boolean): void {
    this.isLoadingTable = isLoadingTable;
  }

  @Mutation
  public setFilters(filters: any): void {
    this.filters = filters;
  }

  @Mutation
  public setQueryObject(queryObject: any): void {
    this.queryObject = queryObject;
  }

  @Mutation
  public setSearch(search: string): void {
    this.search = search;
  }
}
